import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Alert,
  MenuItem,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { 
  ArrowBack, 
  Save, 
  Cancel, 
  Warning, 
  CheckCircle,
  Info,
  EuroSymbol 
} from '@mui/icons-material';
import api from '../services/api';

const AddPosting = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [wageCompliance, setWageCompliance] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    contract_id: '',
    posting_number: '',
    posting_type: 'temporary',
    host_country: 'DE',
    posting_start_date: '',
    posting_end_date: '',
    host_company_name: '',
    host_company_code: '',
    host_company_address: '',
    host_company_contact_person: '',
    host_company_contact_email: '',
    host_company_contact_phone: '',
    a1_form_number: '',
    a1_issue_date: '',
    a1_expiry_date: '',
    minimum_wage_applicable: true,
    actual_wage: '',
    actual_wage_currency: 'EUR',
    actual_wage_type: 'hourly',
    accommodation_provided: false,
    accommodation_address: '',
    transport_provided: false,
    transport_details: '',
    notification_sent: false,
    notification_date: '',
    notification_reference: '',
    status: 'pending',
    notes: ''
  });

  // EU Minimum Wages 2025
  const minimumWages = {
    'DE': { hourly: 12.00, monthly: 2080, country: 'Germany', a1_required: true },
    'NL': { hourly: 13.27, monthly: 2300, country: 'Netherlands', a1_required: true },
    'BE': { hourly: 11.50, monthly: 1990, country: 'Belgium', a1_required: true },
    'FR': { hourly: 11.65, monthly: 2020, country: 'France', a1_required: true },
    'LU': { hourly: 15.50, monthly: 2687, country: 'Luxembourg', a1_required: true },
    'AT': { hourly: 11.00, monthly: 1900, country: 'Austria', a1_required: true },
    'IE': { hourly: 12.70, monthly: 2200, country: 'Ireland', a1_required: true },
    'ES': { hourly: 7.82, monthly: 1350, country: 'Spain', a1_required: true },
    'IT': { hourly: 9.50, monthly: 1650, country: 'Italy', a1_required: true },
    'PL': { hourly: 5.85, monthly: 1015, country: 'Poland', a1_required: true },
    'DK': { hourly: 19.50, monthly: 3380, country: 'Denmark', a1_required: true },
    'SE': { hourly: 13.50, monthly: 2340, country: 'Sweden', a1_required: true },
    'FI': { hourly: 11.00, monthly: 1900, country: 'Finland', a1_required: true }
  };

  useEffect(() => {
    fetchEmployees();
    fetchContracts();
    generatePostingNumber();
  }, []);

  useEffect(() => {
    checkWageCompliance();
  }, [formData.host_country, formData.actual_wage, formData.actual_wage_type]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      const activeEmployees = response.data.filter(emp => emp.status === 'active');
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await api.get('/contracts');
      const activeContracts = response.data.filter(contract => {
        const endDate = new Date(contract.end_date);
        const today = new Date();
        return endDate > today && contract.signed_by_employee && contract.signed_by_employer;
      });
      setContracts(activeContracts);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const generatePostingNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({
      ...prev,
      posting_number: `POST-${year}${month}-${random}`
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEmployeeChange = (event, newValue) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        employee_id: newValue.id
      }));
      
      // Filter contracts for this employee
      const employeeContracts = contracts.filter(c => c.employee_id === newValue.id);
      if (employeeContracts.length === 1) {
        setSelectedContract(employeeContracts[0]);
        setFormData(prev => ({
          ...prev,
          contract_id: employeeContracts[0].id
        }));
      }
    }
  };

  const handleContractChange = (event, newValue) => {
    setSelectedContract(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        contract_id: newValue.id,
        employee_id: newValue.employee_id
      }));
      
      // Set employee based on contract
      const employee = employees.find(e => e.id === newValue.employee_id);
      if (employee) {
        setSelectedEmployee(employee);
      }
    }
  };

  const calculateEndDate = (startDate, days) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + days);
    return end.toISOString().split('T')[0];
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      posting_start_date: startDate,
      // Auto-calculate 183 days (6 months) by default
      posting_end_date: calculateEndDate(startDate, 183)
    }));
  };

  const calculateA1ExpiryDate = (issueDate) => {
    if (!issueDate) return '';
    const issue = new Date(issueDate);
    const expiry = new Date(issue);
    expiry.setFullYear(expiry.getFullYear() + 2); // A1 valid for 2 years
    return expiry.toISOString().split('T')[0];
  };

  const handleA1IssueDateChange = (e) => {
    const issueDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      a1_issue_date: issueDate,
      a1_expiry_date: calculateA1ExpiryDate(issueDate)
    }));
  };

  const checkWageCompliance = () => {
    const countryData = minimumWages[formData.host_country];
    if (!countryData || !formData.actual_wage) {
      setWageCompliance(null);
      return;
    }

    const actualWage = parseFloat(formData.actual_wage);
    const minWage = formData.actual_wage_type === 'hourly' 
      ? countryData.hourly 
      : countryData.monthly;

    setWageCompliance({
      compliant: actualWage >= minWage,
      minWage,
      actualWage,
      country: countryData.country
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.employee_id) {
      setError('Please select an employee');
      return;
    }

    if (!formData.contract_id) {
      setError('Please select a contract');
      return;
    }

    if (!formData.posting_start_date || !formData.posting_end_date) {
      setError('Please enter posting dates');
      return;
    }

    if (!formData.host_company_name) {
      setError('Please enter host company information');
      return;
    }

    // Check wage compliance
    if (wageCompliance && !wageCompliance.compliant) {
      if (!window.confirm(`Warning: The wage is below the minimum wage for ${wageCompliance.country}. Do you want to continue?`)) {
        return;
      }
    }

    try {
      setLoading(true);
      await api.post('/postings/create', formData);
      setSuccess('Posting created successfully!');
      
      setTimeout(() => {
        navigate('/admin/postings');
      }, 2000);
    } catch (error) {
      console.error('Error creating posting:', error);
      setError(error.response?.data?.error || 'Failed to create posting');
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = minimumWages[formData.host_country];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/admin/postings')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          Create New EU Posting
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Minimum Wage Alert */}
      {selectedCountry && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Info color="info" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">
                  {selectedCountry.country} Requirements
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Minimum Wage: €{selectedCountry.hourly}/hour or €{selectedCountry.monthly}/month
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A1 Form: Required for social security coverage
                </Typography>
              </Box>
              {wageCompliance && (
                <Chip
                  icon={wageCompliance.compliant ? <CheckCircle /> : <Warning />}
                  label={wageCompliance.compliant ? 'Wage Compliant' : 'Below Minimum'}
                  color={wageCompliance.compliant ? 'success' : 'error'}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Typography variant="h6" gutterBottom>
            Posting Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Posting Number"
                name="posting_number"
                value={formData.posting_number}
                onChange={handleChange}
                required
                helperText="Auto-generated, can be modified"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Posting Type"
                name="posting_type"
                value={formData.posting_type}
                onChange={handleChange}
                required
              >
                <MenuItem value="temporary">Temporary Posting</MenuItem>
                <MenuItem value="project">Project Based</MenuItem>
                <MenuItem value="training">Training Assignment</MenuItem>
                <MenuItem value="permanent">Permanent Transfer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Employee and Contract Selection */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Employee & Contract
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.personal_code})`}
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Employee"
                    required
                    helperText="Only active employees shown"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={contracts.filter(c => !selectedEmployee || c.employee_id === selectedEmployee.id)}
                getOptionLabel={(option) => `${option.contract_number} - ${option.position_title}`}
                value={selectedContract}
                onChange={handleContractChange}
                disabled={!selectedEmployee}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Contract"
                    required
                    helperText={!selectedEmployee ? "Select employee first" : "Active contracts only"}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Host Country and Duration */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Posting Location & Duration
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Host Country"
                name="host_country"
                value={formData.host_country}
                onChange={handleChange}
                required
              >
                {Object.entries(minimumWages).map(([code, data]) => (
                  <MenuItem key={code} value={code}>
                    {data.country}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                name="posting_start_date"
                type="date"
                value={formData.posting_start_date}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="End Date"
                name="posting_end_date"
                type="date"
                value={formData.posting_end_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                helperText="Max 183 days for tax purposes"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Duration (days)"
                value={
                  formData.posting_start_date && formData.posting_end_date
                    ? Math.ceil((new Date(formData.posting_end_date) - new Date(formData.posting_start_date)) / (1000 * 60 * 60 * 24))
                    : ''
                }
                disabled
                helperText="Auto-calculated"
              />
            </Grid>
          </Grid>

          {/* Host Company Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Host Company Details
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Name"
                name="host_company_name"
                value={formData.host_company_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Code/VAT"
                name="host_company_code"
                value={formData.host_company_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Address"
                name="host_company_address"
                value={formData.host_company_address}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Person"
                name="host_company_contact_person"
                value={formData.host_company_contact_person}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Email"
                name="host_company_contact_email"
                type="email"
                value={formData.host_company_contact_email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Phone"
                name="host_company_contact_phone"
                value={formData.host_company_contact_phone}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Wage Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Wage & Compensation
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Actual Wage"
                name="actual_wage"
                type="number"
                value={formData.actual_wage}
                onChange={handleChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
                error={wageCompliance && !wageCompliance.compliant}
                helperText={
                  wageCompliance && !wageCompliance.compliant
                    ? `Below minimum (€${wageCompliance.minWage})`
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Wage Type"
                name="actual_wage_type"
                value={formData.actual_wage_type}
                onChange={handleChange}
                required
              >
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Currency"
                name="actual_wage_currency"
                value={formData.actual_wage_currency}
                onChange={handleChange}
                required
              >
                <MenuItem value="EUR">EUR</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="minimum_wage_applicable"
                    checked={formData.minimum_wage_applicable}
                    onChange={handleChange}
                  />
                }
                label="Min. Wage Applies"
              />
            </Grid>
          </Grid>

          {/* A1 Form Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            A1 Form (Social Security)
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="A1 Form Number"
                name="a1_form_number"
                value={formData.a1_form_number}
                onChange={handleChange}
                helperText="Required for all EU postings"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="A1 Issue Date"
                name="a1_issue_date"
                type="date"
                value={formData.a1_issue_date}
                onChange={handleA1IssueDateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="A1 Expiry Date"
                name="a1_expiry_date"
                type="date"
                value={formData.a1_expiry_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Auto-calculated (2 years)"
              />
            </Grid>
          </Grid>

          {/* Additional Services */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Additional Services
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="accommodation_provided"
                    checked={formData.accommodation_provided}
                    onChange={handleChange}
                  />
                }
                label="Accommodation Provided"
              />
              {formData.accommodation_provided && (
                <TextField
                  fullWidth
                  label="Accommodation Address"
                  name="accommodation_address"
                  value={formData.accommodation_address}
                  onChange={handleChange}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="transport_provided"
                    checked={formData.transport_provided}
                    onChange={handleChange}
                  />
                }
                label="Transport Provided"
              />
              {formData.transport_provided && (
                <TextField
                  fullWidth
                  label="Transport Details"
                  name="transport_details"
                  value={formData.transport_details}
                  onChange={handleChange}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
          </Grid>

          {/* Host Country Notification */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Host Country Notification
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="notification_sent"
                    checked={formData.notification_sent}
                    onChange={handleChange}
                  />
                }
                label="Notification Sent"
              />
            </Grid>
            {formData.notification_sent && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Notification Date"
                    name="notification_date"
                    type="date"
                    value={formData.notification_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Reference Number"
                    name="notification_reference"
                    value={formData.notification_reference}
                    onChange={handleChange}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Notes */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Additional Notes
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                helperText="Any additional information about this posting"
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate('/admin/postings')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Posting'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default AddPosting;
